import Cookies from 'js-cookie';
import type { CookieAttributes } from 'js-cookie';

const DEFAULT_AUTH_COOKIE_DOMAIN = '.famla.ai';

const AUTH_COOKIE_NAMES = ['access_token', 'refresh_token', 'logged_in', 'is_first_login'] as const;

/**
 * Domain for shared auth cookies across *.famla.ai (omitted on localhost).
 */
export function getAuthCookieDomain(): string | undefined {
    const configured = (
        process.env.REACT_APP_AUTH_COOKIE_DOMAIN ?? DEFAULT_AUTH_COOKIE_DOMAIN
    ).trim();

    if (!configured) {
        return undefined;
    }

    if (typeof window === 'undefined') {
        return configured.startsWith('.') ? configured : `.${configured}`;
    }

    const hostname = window.location.hostname;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
        return undefined;
    }

    const bareDomain = configured.replace(/^\./, '');
    if (hostname === bareDomain || hostname.endsWith(`.${bareDomain}`)) {
        return configured.startsWith('.') ? configured : `.${configured}`;
    }

    return undefined;
}

export function getAuthCookieOptions(extra?: CookieAttributes): CookieAttributes {
    const opts: CookieAttributes = { path: '/', ...extra };
    const domain = getAuthCookieDomain();
    if (domain) {
        opts.domain = domain;
    }
    return opts;
}

export function setAuthCookie(name: string, value: string, extra?: CookieAttributes): void {
    Cookies.set(name, value, getAuthCookieOptions(extra));
}

export function removeAuthCookie(name: string): void {
    Cookies.remove(name, { path: '/' });
    const domain = getAuthCookieDomain();
    if (domain) {
        Cookies.remove(name, { path: '/', domain });
    }
    expireAuthCookieViaDocument(name);
}

function expireAuthCookieViaDocument(name: string): void {
    const expires = 'Thu, 01 Jan 1970 00:00:01 GMT';
    document.cookie = `${name}=; path=/; expires=${expires};`;
    const domain = getAuthCookieDomain();
    if (domain) {
        document.cookie = `${name}=; path=/; domain=${domain}; expires=${expires};`;
    }
    document.cookie = `${name}=; path=/; domain=${window.location.hostname}; expires=${expires};`;
}

export function setAccessTokenCookie(token: string): void {
    setAuthCookie('access_token', token);
}

/** Persist shared auth cookies for cross-app SSO (*.famla.ai subdomains). */
export function persistAuthTokens(accessToken: string, refreshToken?: string): void {
    setAuthCookie('access_token', accessToken);
    setAuthCookie('logged_in', 'True');
    if (refreshToken) {
        setAuthCookie('refresh_token', refreshToken);
    }
}

/** Cookie SSO lisible en JS (access_token peut être HttpOnly côté API). */
export function hasLoggedInCookie(): boolean {
    return Cookies.get('logged_in') === 'True';
}

/** Évite une fausse déconnexion juste après login (course Redux / cookies). */
export function markAuthSyncGracePeriod(ms = 3000): void {
    sessionStorage.setItem('auth_sync_skip_until', String(Date.now() + ms));
}

/**
 * Supprime tous les cookies d'authentification lors de la déconnexion
 * Utilisé pour garantir que les cookies SSO sont bien détruits lors de la déconnexion
 */
export const clearAuthCookies = (): void => {
    AUTH_COOKIE_NAMES.forEach(removeAuthCookie);
    console.log('🍪 [AuthUtils] Tous les cookies d\'authentification ont été supprimés');
};
